import * as THREE from 'three';

// DXF entity types from dxf-parser
interface DXFEntity {
  type: string;
  vertices?: Array<{x: number; y: number; z?: number}>;
  center?: {x: number; y: number; z?: number};
  radius?: number;
  startAngle?: number;
  endAngle?: number;
  layer?: string;
  color?: number;
  colorIndex?: number;
  shape?: boolean;
  // LINE specific
  start?: {x: number; y: number; z?: number};
  end?: {x: number; y: number; z?: number};
  // CIRCLE/ARC specific
  angleLength?: number;
  // 3DFACE specific
  vertices1?: {x: number; y: number; z?: number};
  vertices2?: {x: number; y: number; z?: number};
  vertices3?: {x: number; y: number; z?: number};
  vertices4?: {x: number; y: number; z?: number};
}

interface DXFLayer {
  name: string;
  color?: number;
  colorIndex?: number;
}

interface ParsedDXF {
  entities: DXFEntity[];
  tables?: {
    layer?: {
      layers: Record<string, DXFLayer>;
    };
  };
}

// AutoCAD Color Index → hex color (common colors)
const ACI_COLORS: Record<number, string> = {
  0: '#000000',
  1: '#FF0000',
  2: '#FFFF00',
  3: '#00FF00',
  4: '#00FFFF',
  5: '#0000FF',
  6: '#FF00FF',
  7: '#FFFFFF',
  8: '#808080',
  9: '#C0C0C0',
};

function getColorFromEntity(entity: DXFEntity, layerColor?: number): string {
  const colorIndex = entity.colorIndex ?? entity.color ?? layerColor ?? 7;
  return ACI_COLORS[colorIndex] ?? '#CCCCCC';
}

function createLineMaterial(color: string): THREE.LineBasicMaterial {
  return new THREE.LineBasicMaterial({color});
}

function convertLine(entity: DXFEntity, color: string): THREE.Line | null {
  if (!entity.start || !entity.end) return null;

  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([
    entity.start.x,
    entity.start.y,
    entity.start.z ?? 0,
    entity.end.x,
    entity.end.y,
    entity.end.z ?? 0,
  ]);
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  return new THREE.Line(geometry, createLineMaterial(color));
}

function convertPolyline(entity: DXFEntity, color: string): THREE.Line | null {
  if (!entity.vertices || entity.vertices.length < 2) return null;

  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(entity.vertices.length * 3);

  for (let i = 0; i < entity.vertices.length; i++) {
    const v = entity.vertices[i];
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.y;
    positions[i * 3 + 2] = v.z ?? 0;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  // Close the polyline if it's marked as closed
  if (entity.shape) {
    const indices: number[] = [];
    for (let i = 0; i < entity.vertices.length; i++) {
      indices.push(i);
    }
    indices.push(0); // close
    geometry.setIndex(indices);
  }

  return new THREE.Line(geometry, createLineMaterial(color));
}

function convertCircle(entity: DXFEntity, color: string): THREE.Line | null {
  if (!entity.center || !entity.radius) return null;

  const segments = 64;
  const curve = new THREE.EllipseCurve(
    entity.center.x,
    entity.center.y,
    entity.radius,
    entity.radius,
    0,
    Math.PI * 2,
    false,
    0,
  );

  const points = curve.getPoints(segments);
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(points.length * 3);

  for (let i = 0; i < points.length; i++) {
    positions[i * 3] = points[i].x;
    positions[i * 3 + 1] = points[i].y;
    positions[i * 3 + 2] = entity.center.z ?? 0;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  return new THREE.Line(geometry, createLineMaterial(color));
}

function convertArc(entity: DXFEntity, color: string): THREE.Line | null {
  if (!entity.center || !entity.radius) return null;

  const startAngle = ((entity.startAngle ?? 0) * Math.PI) / 180;
  const endAngle = ((entity.endAngle ?? 360) * Math.PI) / 180;

  const segments = 64;
  const curve = new THREE.EllipseCurve(
    entity.center.x,
    entity.center.y,
    entity.radius,
    entity.radius,
    startAngle,
    endAngle,
    false,
    0,
  );

  const points = curve.getPoints(segments);
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(points.length * 3);

  for (let i = 0; i < points.length; i++) {
    positions[i * 3] = points[i].x;
    positions[i * 3 + 1] = points[i].y;
    positions[i * 3 + 2] = entity.center.z ?? 0;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  return new THREE.Line(geometry, createLineMaterial(color));
}

function convert3DFace(entity: DXFEntity, color: string): THREE.Mesh | null {
  if (!entity.vertices || entity.vertices.length < 3) return null;

  const geometry = new THREE.BufferGeometry();
  const v = entity.vertices;
  const positions: number[] = [];

  // First triangle
  positions.push(v[0].x, v[0].y, v[0].z ?? 0);
  positions.push(v[1].x, v[1].y, v[1].z ?? 0);
  positions.push(v[2].x, v[2].y, v[2].z ?? 0);

  // Second triangle (if 4 vertices)
  if (v.length >= 4) {
    positions.push(v[0].x, v[0].y, v[0].z ?? 0);
    positions.push(v[2].x, v[2].y, v[2].z ?? 0);
    positions.push(v[3].x, v[3].y, v[3].z ?? 0);
  }

  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array(positions), 3),
  );
  geometry.computeVertexNormals();

  const material = new THREE.MeshStandardMaterial({
    color,
    side: THREE.DoubleSide,
    roughness: 0.7,
    metalness: 0.1,
  });

  return new THREE.Mesh(geometry, material);
}

function convertPoint(entity: DXFEntity, color: string): THREE.Points | null {
  if (!entity.vertices || entity.vertices.length === 0) return null;

  // For POINT entities, position might be in the first vertex
  const v = entity.vertices[0];
  if (!v) return null;

  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([v.x, v.y, v.z ?? 0]);
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const material = new THREE.PointsMaterial({color, size: 2});
  return new THREE.Points(geometry, material);
}

export function convertDXFToThreeJS(parsedDXF: ParsedDXF): THREE.Group {
  const root = new THREE.Group();
  root.name = 'DXF_Root';

  const layerGroups: Map<string, THREE.Group> = new Map();

  // Get layer colors
  const layerColors: Record<string, number> = {};
  if (parsedDXF.tables?.layer?.layers) {
    for (const [name, layer] of Object.entries(
      parsedDXF.tables.layer.layers,
    )) {
      layerColors[name] = layer.colorIndex ?? layer.color ?? 7;
    }
  }

  for (const entity of parsedDXF.entities) {
    const layerName = entity.layer ?? '0';
    const layerColor = layerColors[layerName];
    const color = getColorFromEntity(entity, layerColor);

    let object: THREE.Object3D | null = null;

    switch (entity.type) {
      case 'LINE':
        object = convertLine(entity, color);
        break;
      case 'LWPOLYLINE':
      case 'POLYLINE':
        object = convertPolyline(entity, color);
        break;
      case 'CIRCLE':
        object = convertCircle(entity, color);
        break;
      case 'ARC':
        object = convertArc(entity, color);
        break;
      case '3DFACE':
        object = convert3DFace(entity, color);
        break;
      case 'POINT':
        object = convertPoint(entity, color);
        break;
      default:
        // Skip unsupported entity types
        break;
    }

    if (object) {
      object.name = `${entity.type}_${layerName}`;

      // Group by layer
      if (!layerGroups.has(layerName)) {
        const group = new THREE.Group();
        group.name = `Layer_${layerName}`;
        layerGroups.set(layerName, group);
        root.add(group);
      }

      layerGroups.get(layerName)!.add(object);
    }
  }

  return root;
}
